import {
  useFirestore,
  useFirestoreCollectionData,
  useFunctions,
  useUser,
} from 'reactfire'
import { and, collection, or, query, where } from 'firebase/firestore'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Badge,
  Button,
  Col,
  Dropdown,
  Popover,
  Row,
  Space,
  theme,
  Typography,
} from 'antd'
import Icon, {
  BellOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  CommentOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  IssuesCloseOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Party } from './Party'
import dayjs from 'dayjs'
import { createContext, useContext, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorResponse } from '../../lib/errorResponse'
import _orderBy from 'lodash/orderBy'
import 'overlayscrollbars/styles/overlayscrollbars.css'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { useBreakpointContext } from '../../lib/useBreakpointContext'
import { api } from '../../lib/api'
import { getLocalizedNotificationText } from './notificationMessages'
import { useResolvedTheme } from '../../lib/themeController'

const NotificationsContext = createContext({})

export function MarkAllReadButton({ unreadCount, ...buttonProps }) {
  const [dismissingAll, setDismissingAll] = useState(false)
  const functions = useFunctions()
  const { t } = useTranslation()

  async function dismissAll() {
    setDismissingAll(true)
    try {
      await api(functions, 'notifications/dismiss-all', {})
    } catch (error) {
      errorResponse(error)
    } finally {
      setDismissingAll(false)
    }
  }

  if (!unreadCount) return null

  return (
    <Button
      icon={<CheckOutlined />}
      loading={dismissingAll}
      onClick={dismissAll}
      {...buttonProps}
    >
      {t('frontend:notifications.markAllRead')}
    </Button>
  )
}

function NotificationPopoverContent({ notifications }) {
  const { token } = theme.useToken()
  const resolvedTheme = useResolvedTheme()
  const navigate = useNavigate()
  const comp = useRef()
  const { t } = useTranslation()
  const { closePopover } = useContext(NotificationsContext)

  const sortedNotifications = useMemo(
    () => _orderBy(notifications, ['createdAt', 'uid'], ['desc', 'asc']),
    [notifications]
  )

  const dismissableUnreadCount =
    notifications?.filter((n) => !n.dismissedAt && n.userDismissable).length ??
    0

  return (
    <Space ref={comp} direction="vertical" size="middle">
      <Row
        align="middle"
        justify="space-between"
        gutter={token.paddingLG}
        style={{ flexWrap: 'nowrap' }}
      >
        <Col>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('frontend:notifications.title')}
          </Typography.Title>
        </Col>
        <Col>
          <MarkAllReadButton
            unreadCount={dismissableUnreadCount}
            type="link"
            size="small"
          />
        </Col>
      </Row>
      <OverlayScrollbarsComponent
        defer
        options={{
          scrollbars: {
            theme:
              resolvedTheme === 'dark' ? 'os-theme-light' : 'os-theme-dark',
          },
        }}
        style={{ maxHeight: 'min(calc(100vh - 250px), 400px)' }}
      >
        <Space
          ref={comp}
          direction="vertical"
          size="middle"
          style={{
            paddingRight: token.paddingMD,
            width: 'auto',
            maxWidth: '600px',
          }}
        >
          {sortedNotifications.length === 0 ? (
            <Typography.Text
              type="secondary"
              style={{
                textAlign: 'center',
                paddingLeft: token.paddingMD,
                paddingRight: token.paddingMD,
                width: '100%',
              }}
            >
              <Space size="middle">
                {t('frontend:notifications.empty')}
                <Icon style={{ fontSize: '2em' }} component={Party} />
              </Space>
            </Typography.Text>
          ) : (
            sortedNotifications.map((notification) => (
              <NotificationByType
                key={notification.uid}
                notification={notification}
              />
            ))
          )}
        </Space>
      </OverlayScrollbarsComponent>
      <div style={{ textAlign: 'center' }}>
        <Button
          block
          onClick={() => {
            navigate('/my-account/notifications')
            closePopover()
          }}
        >
          {t('frontend:notifications.viewAll')}
        </Button>
      </div>
    </Space>
  )
}

export function Notifications() {
  const firestore = useFirestore()
  const user = useUser()
  const [open, setOpen] = useState(false)
  const [lastClose, setLastClose] = useState(Date.now())

  const { data: notifications } = useFirestoreCollectionData(
    query(
      collection(firestore, 'notifications'),
      and(
        where('userUids', 'array-contains', user.data.uid),
        or(
          where('dismissedAt', '==', null),
          where('dismissedAt', '>', lastClose - 60 * 1000)
        )
      )
    ),
    { idField: 'uid', suspense: false } // Suspense true causes a bug that never stops loading
  )
  const count = notifications?.filter((n) => !n.dismissedAt).length
  const { token } = theme.useToken()

  function afterOpenChange(newOpen) {
    if (!newOpen) {
      // After closing the popup clear the dismissed notifications
      setLastClose(Date.now())
    }
  }

  function closePopover() {
    setOpen(false)
  }

  return (
    <NotificationsContext.Provider value={{ closePopover }}>
      <Popover
        open={open}
        onOpenChange={(newOpen) => setOpen(newOpen)}
        afterOpenChange={afterOpenChange}
        overlayInnerStyle={{
          paddingLeft: token.paddingLG,
          paddingRight: token.paddingLG,
          paddingBlock: token.paddingMD,
          marginLeft: token.marginSM,
          marginRight: token.marginSM,
        }}
        content={<NotificationPopoverContent notifications={notifications} />}
        arrow
        trigger="click"
        placement="bottom"
      >
        <Badge
          style={{ fontWeight: '600', lineHeight: '1.75em' }}
          offset={[0, 5]}
          count={count}
        >
          <Button
            type="text"
            style={{
              color: token.colorText,
              lineHeight: token.lineHeightLG,
              paddingLeft: token.paddingSM,
              paddingRight: token.paddingSM,
            }}
          >
            <BellOutlined style={{ fontSize: token.fontSizeLG }} />
          </Button>
        </Badge>
      </Popover>
    </NotificationsContext.Provider>
  )
}

export function NotificationByType({ notification }) {
  switch (notification.type) {
    case 'new_documents':
      return <NewDocumentsNotification notification={notification} />
    case 'payment_method_missing':
      return <PaymentMethodMissingNotification notification={notification} />
    case 'new_invoice':
    case 'new_consolidated_invoice':
      return <NewInvoiceNotification notification={notification} />
    case 'onboarding_completed':
      return <OnboardingCompletedNotification notification={notification} />
    case 'company_completed':
      return <CompanyCompletedNotification notification={notification} />
    case 'upcoming_payroll_date':
      return <UpcomingPayrollDateNotification notification={notification} />
    case 'employer_immediate_reporting_obligation':
    case 'employee_immediate_reporting_obligation':
    case 'immediate_reporting_obligation':
      return (
        <ImmediateReportingObligationNotification notification={notification} />
      )
    case 'operations_payroll_month_confirmation':
      return (
        <OperationsPayrollMonthConfirmationNotification
          notification={notification}
        />
      )
    case 'new_trial_settlement':
      return <NewTrialSettlementNotification notification={notification} />
    case 'trial_settlement_response':
      return <TrialSettlementResponseNotification notification={notification} />
    case 'leave_request_submitted':
    case 'leave_request_decided':
    case 'sick_report_submitted':
    case 'sick_report_closed':
    case 'sick_note_added':
    case 'absence_correction_decided':
      return <LeaveRequestNotification notification={notification} />
    default:
      return <NotificationFallback notification={notification} />
  }
}

function LeaveRequestNotification({ notification }) {
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const submitted = [
    'leave_request_submitted',
    'sick_report_submitted',
  ].includes(notification.type)
  const noteAdded = notification.type === 'sick_note_added'
  const declined =
    ['declined', 'withdrawn', 'expired'].includes(
      notification.details?.decision
    ) || notification.details?.outcome === 'deleted'

  return (
    <Notification
      notification={notification}
      icon={<CalendarOutlined />}
      iconColor={
        submitted || noteAdded
          ? token.blue5
          : declined
            ? token.orange
            : token.colorSuccess
      }
      buttonText={
        submitted
          ? t('frontend:notifications.viewRequests')
          : t('frontend:notifications.viewAbsences')
      }
    />
  )
}

function NewDocumentsNotification({ notification }) {
  const { token } = theme.useToken()

  return (
    <Notification
      notification={notification}
      icon={<FileOutlined />}
      iconColor={token.blue5}
    />
  )
}

function UpcomingPayrollDateNotification({ notification }) {
  const { token } = theme.useToken()

  return (
    <Notification
      notification={notification}
      icon={<ExclamationCircleOutlined />}
      iconColor={token.blue5}
    />
  )
}

function ImmediateReportingObligationNotification({ notification }) {
  const { token } = theme.useToken()

  return (
    <Notification
      notification={notification}
      icon={<ExclamationCircleOutlined />}
      iconColor={token.orange}
    />
  )
}

function OperationsPayrollMonthConfirmationNotification({ notification }) {
  const { token } = theme.useToken()

  return (
    <Notification
      notification={notification}
      icon={<IssuesCloseOutlined />}
      iconColor={token.green}
    />
  )
}

function PaymentMethodMissingNotification({ notification }) {
  const { token } = theme.useToken()
  const { t } = useTranslation()

  return (
    <Notification
      notification={notification}
      icon={notification?.dismissedAt ? <CheckOutlined /> : <WarningOutlined />}
      iconColor={token.orange}
      buttonText={t('frontend:notifications.addNow')}
      buttonColor={token.orange}
      showTime={false}
    />
  )
}

function OnboardingCompletedNotification({ notification }) {
  const { token } = theme.useToken()
  const { t } = useTranslation()

  return (
    <Notification
      notification={notification}
      icon={<CheckOutlined />}
      iconColor={token.colorSuccess}
      buttonText={t('frontend:notifications.viewData')}
    />
  )
}

function CompanyCompletedNotification({ notification }) {
  const { token } = theme.useToken()
  const { t } = useTranslation()

  return (
    <Notification
      notification={notification}
      icon={<CheckOutlined />}
      iconColor={token.colorSuccess}
      buttonText={t('frontend:notifications.viewData')}
    />
  )
}

function NewInvoiceNotification({ notification }) {
  const { t } = useTranslation()
  return (
    <Notification
      notification={notification}
      icon={<PaperClipOutlined />}
      buttonText={t('frontend:notifications.viewInvoice')}
    />
  )
}

function NewTrialSettlementNotification({ notification }) {
  const { t } = useTranslation()
  return (
    <Notification
      notification={notification}
      icon={<CommentOutlined />}
      buttonText={t('frontend:notifications.view')}
    />
  )
}

function TrialSettlementResponseNotification({ notification }) {
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const approved = notification?.details?.status === 'approved'
  return (
    <Notification
      notification={notification}
      iconColor={approved ? token.colorSuccess : token.colorError}
      icon={approved ? <CheckOutlined /> : <CloseOutlined />}
      buttonText={t('frontend:notifications.view')}
    />
  )
}

function NotificationFallback({ notification }) {
  return <Notification notification={notification} />
}

function Notification({
  notification,
  icon,
  iconColor,
  buttonText,
  buttonColor,
}) {
  const [dismissing, setDismissing] = useState(false)
  const functions = useFunctions()
  const { closePopover } = useContext(NotificationsContext)
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const breakpoint = useBreakpointContext()
  const user = useUser()
  const { t } = useTranslation()

  // Render title/body in the user's language from the notification type/details,
  // falling back to the stored German when unmapped or missing required values.
  const { title: displayTitle, body: displayBody } =
    getLocalizedNotificationText(notification, t)

  async function dismiss(unDismiss = false, shouldClosePopover = true) {
    if (shouldClosePopover && closePopover) {
      closePopover()
    }
    if (!unDismiss && notification.dismissedAt) return
    if (!notification.userDismissable) return
    setDismissing(true)
    try {
      await api(functions, 'notifications/dismiss', {
        notificationUid: notification.uid,
        unDismiss,
      })
    } catch (error) {
      errorResponse(error)
    } finally {
      setDismissing(false)
    }
  }

  return (
    <Row style={{ flexWrap: 'nowrap', width: '100%' }}>
      <Col flex="0 0 50px">
        <Avatar
          icon={dismissing ? <LoadingOutlined /> : icon}
          style={{
            backgroundColor: notification.dismissedAt
              ? 'lightgray'
              : (iconColor ?? token.colorPrimary),
            // paddingTop: '2px',
            // paddingLeft: '1px',
            fontSize: '17px',
            lineHeight: '32px',
          }}
        />
      </Col>
      <Col flex="1 1">
        <Row
          gutter={[token.paddingLG, token.paddingXS]}
          align="middle"
          justify="space-between"
        >
          <Col flex="1 1">
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 'smaller' }}>
                {notification.contextName}
              </Typography.Text>
            </div>
            <div>
              <Typography.Text
                style={{
                  fontWeight: notification.dismissedAt ? '500' : '600',
                  flexGrow: '0',
                  flexShrink: '0',
                }}
              >
                {displayTitle}
              </Typography.Text>
            </div>
            <div>{displayBody}</div>
            <Space wrap>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 'smaller', display: 'block' }}
              >
                {dayjs(notification.createdAt).fromNow()}
              </Typography.Text>
              {notification.dismissedAt && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 'smaller', display: 'block' }}
                >
                  (
                  {notification.userDismissable
                    ? t('frontend:notifications.read')
                    : t('frontend:notifications.resolved')}{' '}
                  {dayjs(notification.dismissedAt).fromNow()}{' '}
                  {t('frontend:common.by')}{' '}
                  {notification.dismissedBy === user.data.uid
                    ? t('frontend:notifications.you')
                    : notification.dismissedByName}
                  )
                </Typography.Text>
              )}
            </Space>
          </Col>
          <Col
            span={breakpoint.xs ? 24 : null}
            style={{ textAlign: breakpoint.xs ? 'left' : 'right' }}
          >
            <Space.Compact>
              {notification.userDismissable || !notification.dismissedAt ? (
                <Button
                  type={notification.dismissedAt ? 'default' : 'primary'}
                  onClick={() => {
                    // noinspection JSIgnoredPromiseFromCall
                    dismiss()
                    navigate(notification.link)
                  }}
                  style={{
                    backgroundColor: notification.dismissedAt
                      ? null
                      : buttonColor,
                  }}
                >
                  {buttonText ?? t('frontend:notifications.view')}
                </Button>
              ) : null}
              {notification.userDismissable && (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'unDismiss',
                        label:
                          notification.dismissedAt !== null
                            ? t('frontend:notifications.markUnread')
                            : t('frontend:notifications.markRead'),
                        onClick: async () => {
                          await dismiss(
                            notification.dismissedAt !== null,
                            false
                          )
                        },
                      },
                    ],
                  }}
                >
                  <Button
                    type={notification.dismissedAt ? 'default' : 'primary'}
                    icon={<EllipsisOutlined />}
                    style={{ backgroundColor: buttonColor }}
                  />
                </Dropdown>
              )}
            </Space.Compact>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}
